// Most-recently-used list of boards the user has opened, for the desktop
// board switcher (render/boards.ts). The per-board snapshot cache
// (board-cache.ts) stores each board's title but has no notion of recency or
// enumeration, so this keeps a small, ordered index alongside it.
//
// Stored as a single localStorage key holding a JSON array, newest first,
// capped at MAX_RECENTS. Best-effort throughout: storage failures are
// swallowed so a blocked/quota'd store never breaks navigation.

import { isValidBoardId } from "../shared/ids";

const STORAGE_KEY = "eyes_recent_boards_v1";
const MAX_RECENTS = 12;

export interface RecentBoard {
    id: string;
    title: string;
}

interface RecentEntry extends RecentBoard {
    ts: number;
}

function read(): RecentEntry[] {
    let raw: string | null;
    try {
        raw = localStorage.getItem(STORAGE_KEY);
    } catch {
        return [];
    }
    if (raw === null) return [];
    try {
        const parsed = JSON.parse(raw) as unknown;
        if (!Array.isArray(parsed)) return [];
        return parsed.filter(isEntry);
    } catch {
        return [];
    }
}

function write(entries: RecentEntry[]): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch {
        // Quota exceeded / storage disabled — ignore.
    }
}

/** Record that `id` was just opened (moving it to the front) and refresh its
 *  title. Called on every board mount. `ts` is monotonic-ish via Date.now();
 *  order is what matters, not the absolute value. */
export function recordRecentBoard(id: string, title: string): void {
    if (!isValidBoardId(id)) return;
    const clean = title.trim() || "Eyes";
    const rest = read().filter((e) => e.id !== id);
    const next: RecentEntry[] = [{ id, title: clean, ts: Date.now() }, ...rest];
    write(next.slice(0, MAX_RECENTS));
}

/** The recent boards, newest first, as `{id, title}` for display. */
export function listRecentBoards(): RecentBoard[] {
    return read().map(({ id, title }) => ({ id, title }));
}

function isEntry(x: unknown): x is RecentEntry {
    if (typeof x !== "object" || x === null) return false;
    const o = x as Record<string, unknown>;
    return (
        typeof o.id === "string" &&
        isValidBoardId(o.id) &&
        typeof o.title === "string" &&
        typeof o.ts === "number"
    );
}
